import React from 'react';
import { MapPin, Star, ExternalLink, Plus, Minus } from 'lucide-react';
import { Route } from '../types';
import { formatDistance, getRouteCardLocation } from '../utils';

interface RouteCardProps {
  route: Route;
  distance?: number;
  isInBelayList?: boolean;
  onToggleBelayList: (route: Route) => void;
  userLocation?: { latitude: number; longitude: number };
  showPitches?: boolean;
  onViewOnMap?: (route: Route) => void;
}

export default function RouteCard({ 
  route, 
  distance, 
  isInBelayList = false, 
  onToggleBelayList,
  userLocation,
  showPitches = false,
  onViewOnMap
}: RouteCardProps) {
  const renderStars = (rating: number) => {
    // Handle invalid or negative ratings
    if (!rating || rating < 0 || isNaN(rating)) {
      return <span className="text-gray-400 text-sm">No rating</span>;
    }


    const stars = [];
    const fullStars = Math.floor(Math.max(0, rating));
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
    }
    
    if (hasHalfStar) {
      stars.push(<Star key="half" className="w-4 h-4 fill-yellow-400/50 text-yellow-400" />);
    }
    
    const emptyStars = Math.max(0, 4 - Math.ceil(rating));
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="w-4 h-4 text-gray-300" />);
    }
    
    return stars;
  };

  const getRouteTypeColor = (type: string) => {
    if (type === 'Sport') return 'bg-blue-100 text-blue-800';
    if (type === 'Trad') return 'bg-green-100 text-green-800';
    return 'bg-purple-100 text-purple-800';
  };

  const getDifficultyColor = (rating: string | number) => {
    const grade = String(rating).toLowerCase();
    
    // Extract numeric part (5.0, 5.1, 5.2, etc.) ignoring letter suffixes
    const numericMatch = grade.match(/5\.(\d+)/);
    if (!numericMatch) return 'bg-gray-100 text-gray-800';
    
    const numericValue = parseInt(numericMatch[1]);
    
    // Color scheme: each numeric value gets a unique color
    switch (numericValue) {
      case 0:
      case 1:
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
      case 7:
        return 'bg-green-100 text-green-800'; // 5.0-5.7: Green
      case 8:
        return 'bg-yellow-100 text-yellow-800'; // 5.8: Yellow
      case 9:
        return 'bg-orange-100 text-orange-800'; // 5.9: Orange
      case 10:
        return 'bg-red-100 text-red-800'; // 5.10: Red
      case 11:
        return 'bg-purple-100 text-purple-800'; // 5.11: Purple
      case 12:
        return 'bg-indigo-100 text-indigo-800'; // 5.12: Indigo
      case 13:
        return 'bg-pink-100 text-pink-800'; // 5.13: Pink
      case 14:
        return 'bg-gray-800 text-white'; // 5.14: Dark gray
      case 15:
        return 'bg-black text-white'; // 5.15: Black
      default:
        return 'bg-gray-100 text-gray-800'; // Fallback
    }
  };

  const pitchLabel = (() => {
    const p = Number(route.Pitches) || 0;
    if (p <= 1) return 'Single Pitch';
    return `${p} pitches`;
  })();

  return (
    <div className="card p-4 hover:shadow-lg transition-shadow duration-200">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-800 mb-1">{route.Route}</h3>
          <div className="flex items-center text-sm text-gray-600 mb-2">
            {onViewOnMap ? (
              <button
                onClick={() => onViewOnMap(route)}
                className="flex items-center text-primary-600 hover:text-primary-700 hover:underline transition-colors cursor-pointer"
                title="View on map"
              >
                <MapPin className="w-4 h-4 mr-1" />
                <span className="truncate">View on Map</span>
              </button>
            ) : (
              <>
                <MapPin className="w-4 h-4 mr-1" />
                <span className="truncate">{getRouteCardLocation(route)}</span>
              </>
            )}
          </div>
        </div>
        <button
          onClick={() => onToggleBelayList(route)}
          className={`ml-2 p-2 rounded-full transition-colors duration-200 ${
            isInBelayList 
              ? 'bg-primary-600 text-white hover:bg-primary-700' 
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
          title={isInBelayList ? 'Remove from My Routes' : 'Add to My Routes'}
        >
          {isInBelayList ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-3">
        {/* Route Details */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Grade:</span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(route.Rating)}`}>
              {route.Rating}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Length:</span>
            <span className="text-sm font-medium">{route.Length} ft</span>
          </div>
        </div>

        {/* Quality and Distance */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Quality:</span>
            <div className="flex items-center">
              {renderStars(route['Avg Stars'])}
              <span className="ml-1 text-sm font-medium">({route['Avg Stars']})</span>
            </div>
          </div>
          
          {distance !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Distance:</span>
              <span className="text-sm font-medium">{formatDistance(distance)}</span>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Mountain Project:</span>
            <a 
              href={route.URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getRouteTypeColor(route['Route Type'])}`}>
          {route['Route Type']}
        </span>
        {route['Route Type'] !== 'Boulder' && (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
            {pitchLabel}
          </span>
        )}
        {route.isClassic && (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
            Classic
          </span>
        )}
      </div>

    </div>
  );
}
